import React from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { ViewGrid } from './ViewGrid';
import { ViewGridTypeEnum } from './ViewGridType';
import { useCellRenderers } from './ViewCellRenderer';
import {
  useViewsContext,
  useGlobalizationContext,
  useCurrentUserContext,
  useGetAllFields,
  useGetView,
  DragContext
} from '@cs-cms/state-management';
import { EDITING_VIEW_FIELD, EditingViewField, EXTENSION_FIELD } from '@cs-cms/types';

interface SystemViewProps {
  indexId?: number;
}

export const SystemView: React.FC<SystemViewProps> = ({ indexId }) => {
  const { editingViewState, setEditingViewState, mutateViewLoading, selectedView, setHasUnsavedChanges } =
    useViewsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { selectedIndex } = useCurrentUserContext();
  const resolvedIndexId = indexId ?? selectedIndex?.id;
  const { data: allFields } = useGetAllFields(selectedLocale, resolvedIndexId);
  const { data: currentViewDetails } = useGetView(selectedView, selectedLocale);
  const [activeItem, setActiveItem] = React.useState<EditingViewField | null>(null);

  const updateFields = (newFields: EditingViewField[]) => {
    if (!setEditingViewState) {
      return;
    }

    setEditingViewState({
      ...editingViewState,
      fields: newFields
    });
    setHasUnsavedChanges?.(true);
  };

  const reorder = (dataItem: EditingViewField) => {
    if (activeItem === dataItem || !activeItem) {
      return;
    }

    const reorderedData = [...editingViewState.fields];
    const prevIndex = reorderedData.findIndex(f => f.id === activeItem.id);
    const nextIndex = reorderedData.findIndex(f => f.id === dataItem.id);

    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(nextIndex, 0, activeItem);

    updateFields(reorderedData);
  };

  const dragStart = (dataItem: EditingViewField) => {
    setActiveItem(dataItem);
  };

  const extensionFieldDisplayName = allFields?.find(field => field.name === EXTENSION_FIELD)?.displayName;

  const cellRenderers = useCellRenderers(
    editingViewState,
    setEditingViewState,
    setHasUnsavedChanges,
    allFields,
    extensionFieldDisplayName
  );

  const handleCellRender = (cellProps: GridCellProps) => {
    if (!cellProps.field) {
      return <td />;
    }

    const field = cellProps.field as keyof (typeof editingViewState.fields)[0];

    const fieldHandlers = {
      [EDITING_VIEW_FIELD.LINK]: () => cellRenderers.renderLinkCell(cellProps),
      [EDITING_VIEW_FIELD.REMOVE]: () => cellRenderers.renderRemoveCell(cellProps),
      [EDITING_VIEW_FIELD.NAME]: () => cellRenderers.renderNameCell(cellProps)
    };

    const handler = fieldHandlers[field as keyof typeof fieldHandlers];
    return handler ? handler() : cellRenderers.renderDefaultCell(cellProps);
  };

  if (!currentViewDetails && editingViewState.fields.length === 0) {
    return (
      <div className="no-system-view-content">
        <p>No system view selected. Please select or create a system view to edit.</p>
      </div>
    );
  }

  return (
    <DragContext.Provider value={{ reorder, dragStart }}>
      <ViewGrid
        type={ViewGridTypeEnum.SYSTEM_VIEW}
        data={editingViewState.fields}
        cellRender={handleCellRender}
        isLoading={mutateViewLoading}
        onChange={updateFields}
      />
    </DragContext.Provider>
  );
};
